import React, { useState, useEffect, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import BlingProductDialog from "../../components/BlingProductDialog";
import PricingTableDialog from "../../components/PricingTableDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function BlingProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bling/products/catalog");
      setProducts(data.items ?? []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (!filter.trim()) return products;
    const q = filter.toLowerCase();
    return products.filter(p =>
      (p.nome || "").toLowerCase().includes(q) ||
      (p.codigo || "").toLowerCase().includes(q)
    );
  }, [products, filter]);

  const handleEdit = (p) => { setEditing(p); setDialogOpen(true); };
  const handleNew  = () => { setEditing(null); setDialogOpen(true); };

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <TextField
          size="small"
          placeholder="Filtrar por nome ou código..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} /> }}
          sx={{ flex: 1, maxWidth: 360 }}
        />
        {!loading && (
          <Typography variant="caption" color="text.secondary">
            {visible.length === products.length
              ? `${products.length} produtos`
              : `${visible.length} de ${products.length}`}
          </Typography>
        )}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew} sx={{ ml: "auto" }}>
          Novo Produto
        </Button>
      </Box>
      {loading ? <CircularProgress /> : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Preço</TableCell>
                <TableCell>Unidade</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.slice(0, 200).map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.nome}</TableCell>
                  <TableCell>{p.codigo || "—"}</TableCell>
                  <TableCell>{p.preco != null ? `R$ ${Number(p.preco).toFixed(2)}` : "—"}</TableCell>
                  <TableCell>{p.unidade || "—"}</TableCell>
                  <TableCell>{p.tipo === "S" ? "Serviço" : "Produto"}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(p)}><EditIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">Nenhum produto encontrado</TableCell></TableRow>
              )}
              {visible.length > 200 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", fontSize: 12 }}>
                    Mostrando 200 de {visible.length} — use o filtro para refinar
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
      <BlingProductDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        product={editing}
        onSaved={load}
      />
    </>
  );
}

function PricingTablesTab() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalog/pricing-tables");
      setTables(Array.isArray(data) ? data : []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byCategory = tables.reduce((acc, t) => {
    const cat = t.category || "Geral";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const handleEdit = (t) => { setEditing(t); setDialogOpen(true); };
  const handleNew  = () => { setEditing(null); setDialogOpen(true); };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Novo Serviço
        </Button>
      </Box>
      {loading ? <CircularProgress /> : (
        Object.entries(byCategory).map(([cat, items]) => (
          <Box key={cat} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: 11 }}>
              {cat}
            </Typography>
            <Paper>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Unidade</TableCell>
                    <TableCell>Faixas</TableCell>
                    <TableCell>Bling</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(t => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.serviceName}</TableCell>
                      <TableCell>{t.unidade}</TableCell>
                      <TableCell>{t.tiers?.length || 0} faixas</TableCell>
                      <TableCell>
                        {t.blingProductId
                          ? <Chip label="● Vinculado" size="small" color="success" variant="outlined" />
                          : <Chip label="○ Não sincronizado" size="small" variant="outlined" />}
                      </TableCell>
                      <TableCell>
                        <Chip label={t.active ? "Ativo" : "Inativo"} size="small" color={t.active ? "success" : "default"} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEdit(t)}><EditIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        ))
      )}
      <PricingTableDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        table={editing}
        onSaved={load}
      />
    </>
  );
}

export default function Catalog() {
  const [tab, setTab] = useState(0);

  return (
    <MainContainer>
      <MainHeader>
        <Title>Catálogo</Title>
        <MainHeaderButtonsWrapper />
      </MainHeader>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Produtos Bling" />
          <Tab label="Serviços com Tabela de Preço" />
        </Tabs>
      </Box>
      <TabPanel value={tab} index={0}><BlingProductsTab /></TabPanel>
      <TabPanel value={tab} index={1}><PricingTablesTab /></TabPanel>
    </MainContainer>
  );
}
